import React, { useState } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import "./AdminPanelSystem.css"; // Import your custom CSS

interface System {
  id: number;
  system_name: string;
  status: string;
  guid: string;
  version?: string;
  onDelete: (id: number) => void;
}

interface AdminPanelSystemsProps {
  systems: System[];
  onDeactivate?: (systemId: number) => Promise<void> | void;
}

const AdminPanelSystems: React.FC<AdminPanelSystemsProps> = ({ systems, onDeactivate }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<System | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const handleShowModal = (system: System) => {
    setSelectedSystem(system);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSystem(null);
    setDeactivating(false);
  };

  const handleDeactivate = async () => {
    if (!selectedSystem) return;
    const msg = `Deactivate "${selectedSystem.system_name}"?\n\nThis will:\n• Delete the system and its license from the database\n• Kick the running app off the server\n• Force it back into license-registration mode on next launch\n• Remove it from your subscription: $50 less on your next invoice (no credit for the rest of this month)\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeactivating(true);
    try {
      await onDeactivate?.(selectedSystem.id);
      handleCloseModal();
    } catch (e) {
      console.error("Deactivate failed:", e);
      setDeactivating(false);
    }
  };

  return (
    <div className="admin-panel-systems">
      {systems.map((system) => {
        const isUnsubscribed = system.status === "unsubscribed";

        return (
          <Card
            key={system.id}
            onClick={() => handleShowModal(system)}
            style={{
              background: isUnsubscribed 
                ? 'rgba(220, 53, 69, 0.05)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: isUnsubscribed
                ? '2px solid #dc3545'
                : '1px solid rgba(33, 150, 243, 0.1)',
              borderRadius: '16px',
              boxShadow: isUnsubscribed
                ? '0 8px 32px rgba(220, 53, 69, 0.2)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              opacity: isUnsubscribed ? 0.7 : 1
            }}
          >
            <Card.Header style={{
              background: isUnsubscribed
                ? 'rgba(220, 53, 69, 0.1)'
                : 'rgba(248, 250, 252, 0.8)',
              border: 'none',
              borderRadius: '16px 16px 0 0',
              padding: '16px 20px',
              borderBottom: isUnsubscribed
                ? '1px solid rgba(220, 53, 69, 0.3)'
                : '1px solid rgba(33, 150, 243, 0.1)',
              fontWeight: '600',
              fontSize: '16px',
              color: isUnsubscribed ? '#dc3545' : '#1565C0'
            }}>
              <span title={system.version ? `App version: v${system.version}` : "Version unknown — system has not connected since version tracking was added"}>
                {system.system_name}
              </span>
            </Card.Header>
            <Card.Body style={{ padding: '20px' }}>
              {isUnsubscribed && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'rgba(220, 53, 69, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(220, 53, 69, 0.2)'
                }}>
                  <small style={{
                    color: '#dc3545',
                    fontWeight: '600',
                    fontSize: '12px'
                  }}>⚠️ This system has been disabled</small>
                </div>
              )}
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <span style={{
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Status</span>
                  <div style={{
                    fontSize: '14px',
                    color: isUnsubscribed ? '#dc3545' : '#10b981',
                    fontWeight: '600'
                  }}>
                    {isUnsubscribed ? 'Unsubscribed' : 'Active'}
                  </div>
                </div>
                
              </div>
            </Card.Body>
          </Card>
        );
      })}

      {/* Modal for showing system details */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>System Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSystem ? (
            <>
              <p>
                <strong>System Name:</strong> {selectedSystem.system_name}
              </p>
              <p>
                <strong>App Version:</strong>{" "}
                {selectedSystem.version
                  ? `v${selectedSystem.version}`
                  : "Unknown — system has not connected since version tracking was added"}
              </p>
              <p>
                <strong>License Key:</strong>{" "}
                {selectedSystem.guid || "No license key available"}
              </p>
            </>
          ) : (
            <p>No system selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedSystem && onDeactivate && (
            <Button
              variant="danger"
              onClick={handleDeactivate}
              disabled={deactivating}
              style={{ marginRight: "auto" }}
            >
              {deactivating ? "Deactivating..." : "Deactivate & Delete"}
            </Button>
          )}
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminPanelSystems;
